import { useMemo } from 'react'
import { useQuery } from '@apollo/client'

import { ACCOUNTS_LIMIT, countUserSites, useSites, useUser } from 'store'

import {
  OPERATION_FIND_SITE,
  OPERATION_FIND_APS,
  OPERATION_FIND_SWITCHES,
  OPERATION_FIND_ESLS,
  OPERATION_FIND_CHROMECASTS,
  OPERATION_FIND_TVS,
  OPERATION_FIND_FIREWALLS,
  OPERATION_FIND_CCTV_CAMERAS,
  OPERATION_FIND_CCTV_SERVERS,
  OPERATION_FIND_INVERTERS
} from '../operations-gql/queries'
import { applyEslStatusResolution } from '../helpers'

export const TYPES = {
  access_points: 'ap',
  switches: 'switch',
  esls: 'esl',
  chromecasts: 'chromecast',
  tvs: 'tv',
  firewalls: 'firewall',
  cctv_servers: 'cctv_server',
  cctv_cameras: 'cctv_camera',
  inverters: 'inverter',
}

const TYPE_OPERATIONS = {
  access_points: OPERATION_FIND_APS,
  switches: OPERATION_FIND_SWITCHES,
  esls: OPERATION_FIND_ESLS,
  chromecasts: OPERATION_FIND_CHROMECASTS,
  tvs: OPERATION_FIND_TVS,
  firewalls: OPERATION_FIND_FIREWALLS,
  cctv_cameras: OPERATION_FIND_CCTV_CAMERAS,
  cctv_servers: OPERATION_FIND_CCTV_SERVERS,
  inverters: OPERATION_FIND_INVERTERS
}

const EMPTY_EQUIPMENTS = []
const emptyEquipments = () => Object.fromEntries(Object.keys(TYPES).map(type => [type, []]))

export const useHasNotSiteSelected = () => {
  const [user] = useUser()
  const [{ selected }] = useSites()
  return useMemo(() => {
    return countUserSites(user) >= ACCOUNTS_LIMIT && selected.length === 0
  }, [user, selected])
}

const deserializeSite = (site) => {
  const { surface_areas } = site;
  const floors = surface_areas.reduce((floors, building) => {
    return [
      ...floors,
      ...building.maps
        .map(({ uuid, name }) => ({ uuid, name, building: { uuid: building.uuid, name: building.name } }))
    ]
  }, [surface_areas])
  return site.devices.map(device => {
    const floor = device.position ? floors.find(({ uuid }) => uuid === device.position.map.uuid) : null
    const { name, type, manufacturer } = device.model || {}
    const { mac, label, status, uuid } = device
    return {
      uuid,
      mac,
      manufacturer,
      model: name,
      label,
      available_level: status,
      type,
      floor_name: floor ? floor.name : null,
      building_name: floor ? floor.building.name : null,
      site
    }
  })
}

const useDeserializeEquipments = ({ apiSite, apiMonitoring, sites, hardwareType }) => {
  const hasNoSiteSelected = useHasNotSiteSelected()
  const monitoringEquipments = apiMonitoring.data?.equipments?.[hardwareType] || EMPTY_EQUIPMENTS
  const statusDependencies = hardwareType === 'esls'
    ? apiMonitoring.data?.equipments?.access_points || EMPTY_EQUIPMENTS
    : EMPTY_EQUIPMENTS

  const unsupervised = useMemo(() => {
    if (sites.length !== 1) {
      return []
    }
    if (apiSite.loading || apiMonitoring.loading || hasNoSiteSelected) {
      return []
    }
    const fromInventory = deserializeSite(apiSite.data.site)
    const equipments = monitoringEquipments.map(equipment => ({ ...equipment, site: sites[0] }))
    const uuids = equipments.map(({ uuid }) => uuid)
    return fromInventory.filter(equipment => equipment.type === TYPES[hardwareType] && !uuids.includes(equipment.uuid))
  }, [apiSite.data, apiMonitoring.loading, monitoringEquipments, sites, hasNoSiteSelected, hardwareType])

  const mergedEquipments = useMemo(() => {
    if (apiSite.loading || apiMonitoring.loading || hasNoSiteSelected) {
      return null
    }

    return {
      ...emptyEquipments(),
      [hardwareType]: monitoringEquipments.concat(sites.length === 1 ? unsupervised : [])
    }
  }, [apiSite.loading, apiMonitoring.loading, monitoringEquipments, unsupervised, sites.length, hasNoSiteSelected, hardwareType])

  const allEquipments = useMemo(() => {
    if (apiSite.loading || apiMonitoring.loading || hasNoSiteSelected) {
      return []
    }
    return [...statusDependencies, ...mergedEquipments[hardwareType]]
  }, [mergedEquipments, statusDependencies, hasNoSiteSelected, hardwareType])

  return useMemo(() => {
    if (apiSite.loading || apiMonitoring.loading || hasNoSiteSelected) {
      return Object.keys(TYPES).reduce((acc, keyMonitoring) => {
        return { ...acc, [keyMonitoring]: [] }
      }, {})
    }
    return Object.entries(mergedEquipments).reduce((acc, [key, values]) => {
      const equipments = applyEslStatusResolution({ equipments: values, allEquipments })
      return {
        ...acc,
        [key]: equipments
      }
    }, {})

  }, [mergedEquipments, allEquipments])
}

const useLoadEquipments = ({ sites, hardwareType = 'access_points' }) => {
  const hasNoSiteSelected = useHasNotSiteSelected()
  const activeHardwareType = TYPE_OPERATIONS[hardwareType] ? hardwareType : 'access_points'

  const apiMonitoring = useQuery(
    TYPE_OPERATIONS[activeHardwareType],
    {
      variables: { sf_ids: sites.map(({ sf_id }) => sf_id) },
      skip: hasNoSiteSelected,
      fetchPolicy: 'cache-first'
    }
  )
  const apiSite = useQuery(OPERATION_FIND_SITE, {
    variables: { sf_id: sites[0].sf_id },
    skip: sites.length !== 1
  })

  const equipments = useDeserializeEquipments({ apiMonitoring, apiSite, sites, hardwareType: activeHardwareType })


  return {
    loading: apiSite.loading || apiMonitoring.loading,
    equipments,
    hasNoSiteSelected
  }
}

export default useLoadEquipments